import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '../../auth/useAuth';
import { createRawApiClient } from '../../api/client';
import { ME_QUERY_KEY, meQueryOptions } from '../../lib/meQuery';
import type { OnboardingFormView } from '../../api/onboardingForm';
import { onStepUpSucceeded } from '../../api/stepUp';
import type { MeResponse, PersonAttributes, Resource, UserPreferences } from '../../api/types';
import { workspaceKey } from '../../lib/workspaceQueryKey';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { workspaceKindFromPacks } from '../../workspace/useWorkspaceKind';
import { TAX_ID_COPIED_PREFERENCE, mergeCopied, runTaxIdCopy, type OnboardingSubject } from './taxIdCopy';

/**
 * Mounts the silent onboarding tax-id copy (`taxIdCopy.ts`, crossmarc/appcore#739) in the app
 * shell. Renders nothing and returns nothing.
 *
 * Two triggers, both for the active FAMILY workspace of a person `/me` lists onboarding
 * invitations for:
 *   1. entering the workspace (sign-in into it, or a switch to it), once per workspace per page
 *      load;
 *   2. every successful step-up ceremony (`onStepUpSucceeded`): right after a ceremony both
 *      tiers hold.
 * Which tier the copy needs is appcore's decision, by the household it lands in (#1978; appcore
 * `onboardingCopyStepUpTier` in `handlers/people.ts`, pinned in its `people-copy.test.ts`; not
 * pinned by any test in this repo):
 *   - a FLOORED household (`assuranceFloor: stepup`): the view tier, 12 hours. That is the tier
 *     the floor already asks of the `GET /people` this run starts with, so a run that gets
 *     as far as the copy is not normally refused for the tier (a step-up that expires or is
 *     revoked between the two calls still is).
 *   - an UNFLOORED household: the reveal tier, five minutes. Trigger 1 then lands only when a
 *     step-up was minted moments before; otherwise the copy meets `STEP_UP_REQUIRED` and the
 *     run stops, quietly, with the subject left for the next trigger.
 * Neither ever opens a ceremony (the people, form and copy calls use the raw client; the
 * `/me` read and the done-write use the intercepted one, and `/me` is not step-up gated), shows
 * anything, or retries on its own. Once every subject is done, a run costs no request at all;
 * a subject that can never be done costs a `GET /people` per trigger (`taxIdCopy.ts`, "Once").
 *
 * The step-up listener defers its run by a macrotask, so the ceremony's own retry (a reveal
 * the user asked for) is dispatched first: the copy spends the same `PERSON_REVEAL` quota, and
 * near the cap the user's request should be the one that lands.
 */
export function useOnboardingTaxIdCopy(): void {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const { activeWorkspaceId, activeMembership, profile, setPreferences } = useWorkspace();
  const meQuery = useQuery(meQueryOptions(token));

  const firmWorkspaceIds = (meQuery.data?.onboardingInvitations ?? []).map(i => i.firmWorkspaceId);
  const firmKey = [...new Set(firmWorkspaceIds)].sort().join(',');
  const eligible = !!token && !!activeWorkspaceId && !!activeMembership && firmKey !== ''
    && workspaceKindFromPacks(activeMembership.packs) !== 'firm';

  // Latest values for the step-up listener, which outlives renders.
  const latest = useRef({ token, activeWorkspaceId, firmWorkspaceIds, eligible, profile, setPreferences });
  latest.current = { token, activeWorkspaceId, firmWorkspaceIds, eligible, profile, setPreferences };
  const running = useRef<Promise<void> | null>(null);
  // A trigger that arrives mid-run (a ceremony succeeding while the entry run is still reading)
  // is not dropped: it runs once more when the current run settles, with the fresh step-up.
  const again = useRef(false);
  const entered = useRef(new Set<string>());

  const runRef = useRef<() => void>(() => undefined);
  runRef.current = () => {
    const cur = latest.current;
    if (!cur.eligible || !cur.token || !cur.activeWorkspaceId) return;
    if (running.current) { again.current = true; return; }
    const tok = cur.token;
    const ws = cur.activeWorkspaceId;
    const raw = createRawApiClient(tok);
    // Narrowed, not cast: a non-array preference would make `.includes` a substring match.
    const done = mergeCopied(queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.[TAX_ID_COPIED_PREFERENCE]
      ?? cur.profile?.preferences?.[TAX_ID_COPIED_PREFERENCE], []);
    running.current = runTaxIdCopy({
      workspaceId: ws,
      firmWorkspaceIds: cur.firmWorkspaceIds,
      done,
      listPeople: () => raw.get<{ items: Resource<PersonAttributes>[] }>('/people', { workspaceId: ws }).then(r => r.items),
      getForm: fw => raw.get<OnboardingFormView>(`/me/onboarding-forms/${encodeURIComponent(fw)}`),
      copy: (personId: string, fw: string, subject: OnboardingSubject) =>
        raw.post<unknown>(`/people/${encodeURIComponent(personId)}/tax-id/copy-from-onboarding`,
          { firmWorkspaceId: fw, subject }, { workspaceId: ws }).then(() => undefined),
      markDone: async keys => {
        // Re-read at write time: appcore replaces a top-level preference whole. `setPreferences`
        // is optimistic and reverts to server truth on a failed PUT without throwing, so a
        // failed write is not logged here. The subject is then tried again next time: while the
        // copied number is still on the row that is a no-op marked done (the row shows a hint),
        // and if someone cleared it in between, it comes back once (the gap `taxIdCopy.ts` names).
        const now = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.[TAX_ID_COPIED_PREFERENCE];
        const patch: Partial<UserPreferences> = { [TAX_ID_COPIED_PREFERENCE]: mergeCopied(now, keys) };
        await latest.current.setPreferences(patch);
      },
      log: (event, fields) => { console.info('[onboarding-tax-id-copy]', event, fields); },
    }).then(result => {
      if (result.copied > 0) void queryClient.invalidateQueries({ queryKey: workspaceKey('people', ws) });
    }, () => undefined).finally(() => {
      running.current = null;
      if (again.current) { again.current = false; runRef.current(); }
    });
  };

  useEffect(() => {
    if (!eligible || !activeWorkspaceId || entered.current.has(activeWorkspaceId)) return;
    entered.current.add(activeWorkspaceId);
    runRef.current();
  }, [eligible, activeWorkspaceId, firmKey]);

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const off = onStepUpSucceeded(() => {
      const t = setTimeout(() => { timers.delete(t); runRef.current(); }, 0);
      timers.add(t);
    });
    return () => { off(); timers.forEach(clearTimeout); };
  }, []);
}
